import { useCallback, useEffect, useRef, useState } from 'react'

interface UseDelayInterface {
  delay: number
  onDelayOver: () => void
  runImmediately?: boolean
}

interface UseDelayResult {
  clear: () => void
  restart: () => void
  start: () => void
  isRunning: () => boolean
  hasRun: boolean
}

export const useDelay = (params: UseDelayInterface): UseDelayResult => {
  const [runIndex, setRunIndex] = useState<number>(0)
  const running = useRef(false)
  const timeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (runIndex === 0 && !params.runImmediately) {
      return
    }

    timeout.current = setTimeout(() => {
      params.onDelayOver()
      running.current = false
    }, params.delay * 1000)

    running.current = true

    return (): void => {
      clearTimeout(timeout.current)
      running.current = false
    }
  }, [params.delay, params.onDelayOver, runIndex, params.runImmediately])

  const clear = useCallback(() => {
    clearTimeout(timeout.current)
    running.current = false
  }, [])

  const restart = useCallback(() => setRunIndex(prev => prev + 1), [])

  const start = restart

  const isRunning = useCallback(() => running.current, [])

  const hasRun = runIndex >= 1

  return {
    clear,
    restart,
    start,
    isRunning,
    hasRun,
  }
}
